// src/pages/PatientLabTestOrderDetailPage.jsx
// Route: /patient/lab-tests/:orderId
// Shows full detail of a single lab test order for the patient portal.
// Data source: GET /api/lab-test-orders/{id} → LabTestOrderDTO.Response

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import { labTestOrderAPI } from '../services/api';

// ── Status colours (matches PatientLabTestsPage) ──────────────────────────
const STATUS_COLORS = {
  ORDERED:          { bg: 'var(--amber-bg)', color: 'var(--amber-text)' },
  CONFIRMED:        { bg: 'var(--green-bg)', color: 'var(--green)' },
  PENDING:          { bg: 'var(--amber-bg)', color: 'var(--amber-text)' },
  COMPLETED:        { bg: 'var(--blue-bg)', color: 'var(--purple)' },
  CANCELLED:        { bg: 'var(--red-bg)', color: 'var(--red)' },
  FAILED:           { bg: 'var(--red-bg)', color: 'var(--red)' },
  SAMPLE_COLLECTED: { bg: 'var(--green-bg)', color: 'var(--green)' },
  REFUND_REQUESTED: { bg: 'var(--amber-bg)', color: 'var(--amber-text)' },
  REFUNDED:         { bg: 'var(--bg-subtle)', color: 'var(--blue)' },
};

function statusStyle(status) {
  return STATUS_COLORS[status?.toUpperCase()] ?? { bg: 'var(--bg-subtle)', color: 'var(--blue)' };
}

// ── Small helpers ─────────────────────────────────────────────────────────
function fmt(date) {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function fmtTime(t) {
  if (!t) return null;
  // t may be "HH:mm:ss" or "HH:mm"
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12  = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

function fmtMoney(amount, currency = 'INR') {
  if (amount == null) return '—';
  const sym = currency === 'INR' ? '₹' : currency;
  return `${sym}${Number(amount).toLocaleString('en-IN')}`;
}

// ── Section card wrapper ──────────────────────────────────────────────────
function Card({ title, children, style }) {
  return (
    <div style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      padding: '20px 24px',
      boxShadow: 'var(--shadow-sm)',
      ...style,
    }}>
      {title && (
        <div style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
          color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 16,
        }}>
          {title}
        </div>
      )}
      {children}
    </div>
  );
}

// ── Key-value row ─────────────────────────────────────────────────────────
function KV({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between',
      alignItems: 'flex-start', gap: 12, paddingBottom: 10,
      borderBottom: '1px solid var(--border)', marginBottom: 10 }}>
      <span style={{ fontSize: 13, color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500,
        textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}

// ── Live partner status ──────────────────────────────────────────────────
// The 4-step stepper only knows Previu's own status. The lab partner reports
// finer-grained progress (assigned, on the way, arrived, sample at the lab...)
// via partnerOrderStatus, which the backend maps down onto those four steps
// (ThyrocarePartnerOrderStatusMapper). This surfaces the finer step as one
// plain-language line so the tracker no longer sits at "Confirmed" while a
// collector is already at the door. Keys are canonicalised the same way the
// backend does it (uppercase, non-alphanumerics stripped), so "Yet to Assign",
// "YET TO ASSIGN" and "yet_to_assign" all match.
const PARTNER_STATUS_TEXT = {
  YETTOASSIGN:     'Waiting for a collector to be assigned',
  ORDERBOOKED:     'Waiting for a collector to be assigned',
  ASSIGNED:        'A collector has been assigned',
  ORDERASSIGNED:   'A collector has been assigned',
  STARTED:         'Your collector is on the way',
  PHLEBOSTARTED:   'Your collector is on the way',
  ARRIVED:         'Your collector has arrived',
  PHLEBOARRIVED:   'Your collector has arrived',
  RESCHEDULED:     'Your collection was rescheduled',
  ORDERRESCHEDULED:'Your collection was rescheduled',
  SERVICED:        'Sample collected',
  SAMPLECOLLECTED: 'Sample collected',
  SAMPLEIMPORTED:  'Sample received at the lab. Your report is being prepared',
  DONE:            'Report ready. It appears in My Documents',
  REPORTFULL:      'Report ready. It appears in My Documents',
  COMPLETED:       'Report ready. It appears in My Documents',
};

function partnerStatusText(partnerStatus) {
  if (!partnerStatus) return null;
  return PARTNER_STATUS_TEXT[String(partnerStatus).toUpperCase().replace(/[^A-Z0-9]/g, '')] ?? null;
}

// Statuses the backend's LabTestReminderScheduler still sends WhatsApp
// reminders for (1 day and 1 hour before collection).
const REMINDER_STATUSES = ['ORDERED', 'CONFIRMED'];

// ── Status timeline step ──────────────────────────────────────────────────
const STATUS_STEPS = [
  'ORDERED', 'CONFIRMED', 'SAMPLE_COLLECTED', 'COMPLETED',
];

function StatusTimeline({ currentStatus }) {
  const upper = currentStatus?.toUpperCase();
  const isCancelled  = upper === 'CANCELLED' || upper === 'FAILED';
  const isRefund     = upper === 'REFUND_REQUESTED' || upper === 'REFUNDED';
  const currentIndex = STATUS_STEPS.indexOf(upper);

  if (isCancelled || isRefund) {
    const s = statusStyle(upper);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 16px', borderRadius: 8, background: s.bg }}>
        <span style={{ fontSize: 18 }}>{isCancelled ? '✕' : '↩'}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: s.color }}>
          {upper === 'REFUND_REQUESTED' ? 'Refund Requested'
            : upper === 'REFUNDED' ? 'Refunded'
            : upper === 'FAILED' ? 'Order Failed'
            : 'Order Cancelled'}
        </span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {STATUS_STEPS.map((step, i) => {
        const done    = currentIndex >= i;
        const current = currentIndex === i;
        const labels  = {
          ORDERED: 'Ordered',
          CONFIRMED: 'Confirmed',
          SAMPLE_COLLECTED: 'Sample Collected',
          COMPLETED: 'Completed',
        };
        return (
          <React.Fragment key={step}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: done ? 'var(--brand-accent)' : 'var(--border)',
                border: current ? '3px solid var(--brand-accent)' : '2px solid transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: current ? '0 0 0 3px rgba(184,132,58,0.2)' : 'none',
                transition: 'all 0.2s',
              }}>
                {done && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="var(--bg-surface)" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <div style={{ fontSize: 11, marginTop: 6, textAlign: 'center',
                color: done ? 'var(--brand-accent)' : 'var(--text-muted)',
                fontWeight: done ? 600 : 400, lineHeight: 1.3, maxWidth: 70 }}>
                {labels[step]}
              </div>
            </div>
            {i < STATUS_STEPS.length - 1 && (
              <div style={{
                height: 2, flex: 1, marginBottom: 22,
                background: currentIndex > i ? 'var(--brand-accent)' : 'var(--border)',
                transition: 'background 0.2s',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function PatientLabTestOrderDetailPage() {
  const { orderId } = useParams();
  const navigate    = useNavigate();
  const [order, setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  async function handleDownloadInvoice() {
    if (!order?.patientId || downloadingInvoice) return;
    setDownloadingInvoice(true);
    try {
      const res = await labTestOrderAPI.downloadInvoice(order.patientId, orderId);
      const win = window.open(res.data.downloadUrl, '_blank', 'noopener');
      if (!win) setError('Pop-up blocked — please allow pop-ups for this site and try again.');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not generate an invoice for this order.');
    } finally {
      setDownloadingInvoice(false);
    }
  }

  const fetchOrder = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setError('');
    try {
      const res = await labTestOrderAPI.get(orderId);
      setOrder(res.data || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load order details.');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  return (
    <PatientAppShell breadcrumbs={[
      { label: 'My Lab Tests', to: '/patient/lab-tests' },
      { label: order?.planName ?? order?.testCode ?? 'Order Detail' },
    ]}>
      {/* Back link */}
      <button
        onClick={() => navigate('/patient/lab-tests')}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--brand-accent)', fontSize: 13, fontWeight: 600,
          padding: 0, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        ← Back to My Lab Tests
      </button>

      {loading && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
          Loading order…
        </div>
      )}

      {error && (
        <div style={{ padding: 16, background: 'var(--red-bg)', borderRadius: 8,
          color: 'var(--red)', fontSize: 14, marginBottom: 20 }}>
          {error}
          <button onClick={fetchOrder} style={{ marginLeft: 12, background: 'none',
            border: 'none', color: 'var(--red)', cursor: 'pointer', fontWeight: 600,
            textDecoration: 'underline' }}>
            Retry
          </button>
        </div>
      )}

      {!loading && order && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* ── Hero header ── */}
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
              <div>
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700,
                  color: 'var(--text-primary)', margin: 0, marginBottom: 4 }}>
                  {order.planName ?? order.testCode ?? 'Lab Test Order'}
                </h1>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {order.testCode && <span>Code: <strong>{order.testCode}</strong></span>}
                  {order.partnerOrderId && (
                    <span style={{ marginLeft: 10 }}>
                      · Thyrocare Ref: <strong>{order.partnerOrderId}</strong>
                    </span>
                  )}
                  {order.partnerRefOrderNo && (
                    <span style={{ marginLeft: 10 }}>
                      · Ref: <strong>{order.partnerRefOrderNo}</strong>
                    </span>
                  )}
                </div>
              </div>
              <span style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700,
                ...statusStyle(order.status),
              }}>
                {order.status ?? 'Unknown'}
              </span>
            </div>

            {/* Status timeline */}
            <StatusTimeline currentStatus={order.status} />

            {partnerStatusText(order.partnerOrderStatus) && (
              <div className="alert alert-info" style={{ marginTop: 16, marginBottom: 0 }}>
                <strong>Latest update:</strong> {partnerStatusText(order.partnerOrderStatus)}
              </div>
            )}
            {REMINDER_STATUSES.includes(order.status?.toUpperCase()) && (
              <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
                We will remind you on WhatsApp 1 day and 1 hour before your collection.
              </div>
            )}
          </Card>

          {/* ── 2-col grid for details ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>

            {/* Test details */}
            <Card title="Test Details">
              <KV label="Plan"         value={order.planName} />
              <KV label="No. of Tests" value={order.noOfTests} />
              <KV label="Fasting"      value={order.fastingRequired === 'Yes' ? 'Required' : order.fastingRequired === 'No' ? 'Not required' : order.fastingRequired} />
              <KV label="Ordered on"   value={fmt(order.orderAudit?.createdTs)} />
              <KV label="Partner"      value={order.partnerProvider} />
              <KV label="Partner Status" value={order.partnerOrderStatus} />
            </Card>

            {/* Appointment — including WHERE, which this card never showed.
                 The API only returned collectionAddressId, a bare UUID, so
                 there was nothing displayable and the page said nothing at all
                 about where a phlebotomist was coming. collectionAddress
                 (backend V87) carries the real lines. */}
            <Card title="Appointment">
              <KV label="Date"       value={fmt(order.appointmentDate)} />
              <KV label="Time"       value={fmtTime(order.appointmentTime)} />
              <KV label="End Time"   value={fmtTime(order.appointmentEndTime)} />
              <KV label="Time Zone"  value={order.appointmentTimeZone} />

              {order.collectionAddress ? (
                <div style={{ paddingTop: 4 }}>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
                    Collection address
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500,
                    lineHeight: 1.6 }}>
                    {[order.collectionAddress.addressLine1,
                      order.collectionAddress.addressLine2]
                      .filter(Boolean).join(', ')}
                    {order.collectionAddress.landmark
                      ? <>, near {order.collectionAddress.landmark}</> : null}
                    <br />
                    {[order.collectionAddress.city, order.collectionAddress.state]
                      .filter(Boolean).join(', ')}
                    {/* Pincode on its own line — it is what slot availability
                        was checked against and where the phlebotomist is sent,
                        so it is the line worth being able to verify. */}
                    {order.collectionAddress.pincode && (
                      <>
                        <br />
                        <span style={{ fontWeight: 700 }}>
                          Pincode {order.collectionAddress.pincode}
                        </span>
                      </>
                    )}
                  </div>
                  {/* fromOrderSnapshot=false means these lines were read off the
                      patient's saved address because the order predates the
                      snapshot — that row may have been edited since, so say so
                      rather than presenting it as the collection location. */}
                  {order.collectionAddress.fromOrderSnapshot === false && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6,
                      fontStyle: 'italic' }}>
                      From your saved address — this order predates address
                      recording, so it may have changed since.
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  No collection address recorded for this order. Please check with
                  your health navigator before the visit.
                </div>
              )}

              {!order.appointmentDate && (
                <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic',
                  marginTop: 10 }}>
                  Appointment details not available yet.
                </div>
              )}
            </Card>

            {/* Payment */}
            <Card title="Payment">
              <KV label="Amount"     value={fmtMoney(order.price, order.currency)} />
              <KV label="Currency"   value={order.currency} />
              <KV label="Payment ID" value={order.razorpayPaymentId} />
              <KV label="Order ID"   value={order.razorpayOrderId} />
              {order.razorpayPaymentId && (
                <button
                  type="button"
                  onClick={handleDownloadInvoice}
                  disabled={downloadingInvoice}
                  style={{
                    marginTop: 12, background: 'none', border: '1px solid var(--brand-accent)',
                    borderRadius: 'var(--radius-sm)', padding: '7px 14px', fontSize: 13, fontWeight: 600,
                    color: 'var(--brand-accent)', cursor: downloadingInvoice ? 'not-allowed' : 'pointer',
                  }}
                >
                  {downloadingInvoice ? 'Generating…' : '⬇ Download Invoice'}
                </button>
              )}
            </Card>

            {/* Patient info */}
            <Card title="Patient Details">
              <KV label="Name"   value={order.patientName} />
              <KV label="Phone"  value={order.patientPhone} />
              <KV label="Email"  value={order.patientEmail} />
            </Card>

          </div>

          {/* Help note */}
          <div style={{ padding: '14px 18px', background: 'var(--bg-canvas)',
            borderRadius: 8, fontSize: 13, color: 'var(--text-muted)',
            border: '1px solid var(--border)' }}>
            💬 For any queries about your order, please contact your health navigator or
            use <strong>Ask Previu</strong>.
          </div>

        </div>
      )}
    </PatientAppShell>
  );
}
